import { TableLowcode } from "alurkerja-ui";
import React, {
  ComponentProps,
  ForwardedRef,
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Null } from "../null";
import { detailAnchor } from "./detail-anchor";
import { editAnchor } from "./edit-anchor";
import { TableInstance, TableSpec } from "alurkerja-ui/dist/types";
import { Pagination, TablePagination } from "@mui/material";
import { Paginated } from "@/api/types";
import { DataConfig } from "./useDefaultDataConfig";
import { handleFilterByColumns } from "@/utils/helper/handleFilterByColumns";

interface PaginatedData extends Paginated {
  content: { [key: string]: unknown }[];
}
type TableLowCodeProps = ComponentProps<typeof TableLowcode>;
interface TableDefaultProps
  extends Omit<TableLowCodeProps, "data" | "baseUrl"> {
  spec: TableSpec;
  dataConfig?: DataConfig;
  setDataConfig?: React.Dispatch<React.SetStateAction<DataConfig>>;
  data?: PaginatedData;
  className?: string;
}
const TableDefault = forwardRef(
  (props: TableDefaultProps, ref: ForwardedRef<TableInstance>) => {
    const [selectedRow, setSelectedRow] = useState<number[]>(
      props.selectedRow ?? [],
    );

    const [filterBy, setFilterBy] = useState<{ [x: string]: string }>();

    const fields = props.spec.fields as unknown as {
      [key: string]: { [key: string]: string };
    };
    const fieldsArray = Object.keys(fields)
      .map((key) => {
        return {
          name: key,
          label: fields[key].label,
          sortable: fields[key].sortable,
          sortName: fields[key].sortName,
        };
      })
      .filter((field) => field.name !== "custom_no");

    const currentPage = props.dataConfig?.page
      ? parseInt(props.dataConfig.page, 10)
      : 0;

    const [limitPerPage, setLimitPerPage] = useState(10);

    const dataWithNumber = useMemo(() => {
      return props.data?.content?.map((data, index) => {
        return {
          ...data,
          custom_no: index + 1 + currentPage * limitPerPage,
        };
      });
    }, [props.data, currentPage, limitPerPage]);

    useEffect(() => {
      const main = document.querySelector("main");
      const listener = generateSortListener(
        fieldsArray as unknown as {
          label: string;
          sortable: boolean;
          sortName?: string;
          name: string;
        }[],
        props.setDataConfig,
      );
      main?.addEventListener("click", listener);

      return () => {
        main?.removeEventListener("click", listener);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // handle filter by columns
    useEffect(() => {
      if (filterBy) {
        handleFilterByColumns(filterBy, props.setDataConfig, props.dataConfig);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterBy]);

    return (
      <div className={props.className + " rounded bg-white"}>
        <TableLowcode
          ref={ref}
          spec={props.spec}
          baseUrl={import.meta.env.VITE_API_BASE}
          headerElement={props.headerElement ?? <Null />}
          selectedRow={props.selectedRow ?? selectedRow}
          setSelectedRow={props.setSelectedRow ?? setSelectedRow}
          labelAction="Action"
          customButtonDetail={props.customButtonDetail ?? detailAnchor()}
          customButtonEdit={props.customButtonEdit ?? editAnchor()}
          data={dataWithNumber}
          column={props.column}
          hideActionColumn={props.hideActionColumn}
          customCell={props.customCell}
          customBulkCell={props.customBulkCell}
          filterBy={filterBy}
          setFilterBy={setFilterBy}
          enableResponsive={false}
          searchFilterByColumns={props.searchFilterByColumns}
          customFilterByColumns={props.customFilterByColumns}
          loading={props.loading ?? false}
          stickyHeader
          stickyFooter
          classNameTableWrapperSticky="max-h-[calc(100vh-18rem)] overflow-y-auto"
          hideNumberIteration
          loadingSkeleton
        />
        <div className="flex items-center justify-between rounded">
          <TablePagination
            component="div"
            count={props.data?.totalPages ?? 0}
            page={props.dataConfig?.page ? parseInt(props.dataConfig.page) : 0}
            rowsPerPageOptions={[10, 25, 50, 75, 100]}
            onPageChange={() => {}}
            rowsPerPage={
              props.dataConfig?.limit
                ? parseInt(props.dataConfig.limit, 10)
                : 10
            }
            onRowsPerPageChange={(e) => {
              props.setDataConfig?.((prev) => ({
                ...prev,
                limit: e.target.value,
                page: "0",
              }));

              setLimitPerPage(parseInt(e.target.value, 10));
            }}
            labelDisplayedRows={Null}
            slots={{
              actions: {
                nextButton: Null,
                previousButton: Null,
              },
            }}
          />
          <Pagination
            count={props.data?.totalPages}
            page={
              props.dataConfig?.page
                ? parseInt(props.dataConfig.page, 10) + 1
                : 1
            }
            onChange={(_, page: number) => {
              props.setDataConfig?.((prev) => ({
                ...prev,
                page: (page - 1).toString() ?? "0",
              }));
            }}
            showFirstButton
            showLastButton
          />
        </div>
      </div>
    );
  },
);

export { TableDefault };

function generateSortListener(
  fieldsArray: {
    label: string;
    sortable: boolean;
    sortName?: string;
    name: string;
  }[],
  setDataConfig?: React.Dispatch<React.SetStateAction<DataConfig>>,
) {
  return (e: MouseEvent) => {
    const path = e.composedPath();
    const th = path.find((el) => (el as HTMLElement).nodeName === "TH");
    if (!th) {
      return;
    }
    const field = fieldsArray.find(
      (field) => field.label === (th as HTMLElement)?.textContent,
    );

    if (!field?.sortable) {
      return;
    }

    setDataConfig?.((prev) => ({
      ...prev,
      sort: field.sortName ?? field.name,
      asc: prev.asc === "false" ? "true" : "false",
    }));
  };
}
